import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { 
  Field, 
  Tag, 
  TagPicker, 
  TagPickerControl, 
  TagPickerGroup, 
  TagPickerInput, 
  TagPickerList, 
  TagPickerOption, 
  TagPickerProps,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';

import { Protocol } from '../../../../models/Entities/Protocols/Protocol';
import * as Functions from './ProtocolSelectorFunctions';

const ProtocolSelectorComponent = (props: Functions.Props) => {
  // Store selected protocol ids as strings to keep TagPicker values unique
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [filterQuery, setFilterQuery] = useState<string>('');
  
  // Ref para prevenir actualizaciones circulares
  const isInternalUpdate = useRef(false);
  const previousProtocolsRef = useRef<Protocol[]>([]);

  // Cargar protocolos al montar el componente
  useEffect(() => {
    props.GetAllMedicalProtocols();
  }, []); // Solo una vez al montar

  // Función para comparar arrays de protocolos
  const areProtocolsEqual = useCallback((protocols1: Protocol[] | undefined, protocols2: Protocol[]): boolean => {
    if (!protocols1 || protocols1.length !== protocols2.length) return false;
    const ids1 = protocols1.map(p => p.id).sort();
    const ids2 = protocols2.map(p => p.id).sort();
    return ids1.every((id, index) => id === ids2[index]);
  }, []);

  // Sincronizar selectedOptions cuando selectedProtocols cambia (desde el padre)
  useEffect(() => {
    // Evitar actualización si el cambio vino de este componente
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (!props.Protocols || props.Protocols.length === 0) return;

    if (props.selectedProtocols && props.selectedProtocols.length > 0) {
      const selectedIds = props.selectedProtocols
        .map((protocol: Protocol) => protocol.id?.toString())
        .filter((id): id is string => id !== undefined);
      
      // Solo actualizar si realmente cambió
      if (JSON.stringify(selectedIds.sort()) !== JSON.stringify(selectedOptions.sort())) {
        setSelectedOptions(selectedIds);
      }
    } else {
      // Limpiar selección si no hay selección en props
      if (selectedOptions.length > 0) {
        setSelectedOptions([]);
      }
    }
  }, [props.selectedProtocols, props.Protocols]);

  const onInputChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    setFilterQuery(e.currentTarget.value);
  }, []);

  // Memoizar las opciones disponibles del TagPicker (filtradas por búsqueda)
  const tagPickerOptions = useMemo(() => {
    if (!props.Protocols || props.Protocols.length === 0) return [];
    
    return props.Protocols.filter((protocol: Protocol) => !selectedOptions.includes(protocol.id.toString()) && 
      protocol.title.toLowerCase().includes(filterQuery.toLowerCase())
    );
  }, [props.Protocols, selectedOptions, filterQuery]);

  // Callback para manejar la selección de opciones
  const onOptionSelect: TagPickerProps["onOptionSelect"] = useCallback((e, data) => {
        
    if (data.selectedOptions.length === 0) {
      setSelectedOptions([]);
      props.onChangeSelection && props.onChangeSelection([]);
      return;
    }

    let newSelectedOptions: string[];
    
    // Detectar si es una operación de remoción (dismiss) o adición
    const isRemoving = data.selectedOptions.length < selectedOptions.length;
    
    if (isRemoving) {
      // Si se está removiendo, usar directamente selectedOptions del evento
      newSelectedOptions = data.selectedOptions;
    } else {
      // Si se está agregando
      if (props.selectionLimit === 1) {
        // Modo selección única: reemplazar con el nuevo valor
        newSelectedOptions = [data.value];
      } else {
        // Modo multi-selección
        newSelectedOptions = data.selectedOptions;
        
        // Verificar límite de selección
        if (props.selectionLimit && newSelectedOptions.length > props.selectionLimit) {
          return; // No permitir más de lo especificado
        }
      }
    }

    setSelectedOptions(newSelectedOptions);

    

    // Obtener los objetos Protocol correspondientes
    const selectedProtocols = newSelectedOptions.length > 0
      ? props.Protocols?.filter((protocol: Protocol) => 
          newSelectedOptions.includes(protocol.id.toString())
        ) || []
      : [];

    // Comparar con la selección anterior para evitar actualizaciones innecesarias
    // Si no hay selección, siempre notificar el cambio para que el array vacío se propague
    const shouldNotify = newSelectedOptions.length === 0 
      ? previousProtocolsRef.current.length > 0 
      : !areProtocolsEqual(selectedProtocols, previousProtocolsRef.current);

    if (shouldNotify) {
      previousProtocolsRef.current = selectedProtocols;
      isInternalUpdate.current = true;
      if (props.onChangeSelection) {
        props.onChangeSelection(selectedProtocols);
      }
    }
  }, [props.selectionLimit, props.Protocols, props.onChangeSelection, areProtocolsEqual, selectedOptions]);

  // Renderizar spinner de carga
  if (props.isLoading) {
    return (
      <Field style={{ maxWidth: 400 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
          <Spinner size="tiny" label="Cargando protocolos médicos..." />
        </div>
      </Field>
    );
  }

  // Renderizar error si falló la carga
  if (props.failOnLoading && props.error) {
    return (
      <Field style={{ maxWidth: 400 }}>
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Error al cargar protocolos</MessageBarTitle>
            {props.error.ErrorMessage || 'No se pudieron cargar los protocolos médicos. Por favor, intente nuevamente.'}
          </MessageBarBody>
        </MessageBar>
      </Field>
    );
  }

  // Verificar si no hay protocolos disponibles
  const hasNoProtocols = !props.Protocols || props.Protocols.length === 0;

  return (
    <Field 
      style={{ maxWidth: 400 }}
      hint={props.selectionLimit ? `Puede seleccionar hasta ${props.selectionLimit} protocolo(s)` : undefined}
    >
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptions}
        disabled={hasNoProtocols}
      >
        <TagPickerControl>
          <TagPickerGroup aria-label="Protocolos seleccionados">
            {selectedOptions.map((option) => {
              const selectedProtocol = props.Protocols?.find((protocol: Protocol) => protocol.id.toString() === option);
              if (!selectedProtocol) {
                return null;
              }

              return (
                <Tag
                  key={selectedProtocol.id}
                  shape="rounded"
                  value={option}
                  dismissible
                  dismissIcon={<DismissRegular />}
                >
                  {selectedProtocol.title}
                </Tag>
              );
            })}
          </TagPickerGroup>
          <TagPickerInput 
            aria-label="Seleccione protocolos médicos"
            placeholder={
              hasNoProtocols 
                ? "No hay protocolos disponibles" 
                : selectedOptions.length === 0 
                  ? "Buscar protocolos..." 
                  : ""
            }
            onKeyUp={onInputChange}
          />
        </TagPickerControl>
        <TagPickerList>
          {tagPickerOptions.length > 0 ? (
            tagPickerOptions.map((option) => (
              <TagPickerOption
                value={option.id.toString()}
                key={option.id}
                text={option.title}
              >
                {option.title}
              </TagPickerOption>
            ))
          ) : (
            <TagPickerOption value="no-options" text="">
              {hasNoProtocols ? "No hay protocolos disponibles" : "No hay más protocolos"}
            </TagPickerOption>
          )}
        </TagPickerList>
      </TagPicker>
    </Field>
  );
};

export default connect(
  Functions.mapStateToProps,
  Functions.mapDispatchToProps
)(ProtocolSelectorComponent as any);
